/* CB Radio Supply cart guard. Unpublished release candidate, 2026-10-28. */
(() => {
  'use strict';
  const props = item => item.properties || {};
  const group = item => props(item)._cbrs_group;
  const role = item => props(item)._cbrs_role;
  const managed = item => props(item)._cbrs_version === '1' && !!group(item);
  const integer = n => Number.isInteger(Number(n)) && Number(n) >= 0;
  function planChange(cart, key, quantity) {
    if (!integer(quantity)) throw Error('Please enter a whole-number quantity.');
    quantity = Number(quantity);
    const item = cart.items.find(x => x.key === key);
    if (!item) throw Error('Your cart changed. Please refresh before trying again.');
    const updates = {[key]: quantity};
    if (!managed(item)) return updates;
    // Explicitly removing a visible service must remain possible even if a
    // previous partial add left no matching parent. Never infer another parent.
    if (role(item) === 'tune' && quantity === 0) return updates;
    const related = cart.items.filter(x => managed(x) && group(x) === group(item));
    const parents = related.filter(x => role(x) === 'radio');
    if (parents.length !== 1 ||
        String(parents[0].variant_id) !== String(props(parents[0])._cbrs_parent_variant)) {
      throw Error('This package needs a cart review before its linked options can be updated. Please contact us for help.');
    }
    const parent = parents[0];
    const services = related.filter(x => role(x) === 'tune');
    if (role(item) === 'tune') {
      const total = services.reduce((n, s) => n + (s.key === key ? quantity : s.quantity), 0);
      if (total > parent.quantity) throw Error('Attached Pro Tune quantity cannot exceed the number of radios in this package.');
    } else if (role(item) === 'radio') {
      // Do not add paid services when radio quantity increases.
      let remaining = quantity;
      for (const service of services) {
        const next = Math.min(service.quantity, remaining);
        remaining -= next;
        if (next !== service.quantity) updates[service.key] = next;
      }
    }
    return updates;
  }
  const core = {planChange, managed};
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof document === 'undefined') return;
  if (window.CBRSCartGuard) return;
  window.CBRSCartGuard = {version:'20261028.1'};
  const base = window.Shopify?.routes?.root || '/';
  let busy = false;
  const isVisible = el => !!el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden';
  const stop = event => {event.preventDefault(); event.stopImmediatePropagation();};
  function message(host, text, review = false) {
    let box = host.querySelector('[data-cbrs-message]');
    if (!box) {
      box = document.createElement('div');
      box.dataset.cbrsMessage = '';
      box.setAttribute('role', 'alert');
      box.setAttribute('tabindex', '-1');
      box.style.cssText = 'padding:12px;margin:12px 0;border:1px solid currentColor;background:#fff;color:#222';
      host.prepend(box);
    }
    box.replaceChildren(document.createTextNode(text));
    if (review) {
      const a = document.createElement('a');
      a.href = base + 'cart';
      a.textContent = ' Review your cart';
      box.append(a);
    }
    box.focus({preventScroll:false});
  }
  async function request(path, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(base + path, {
        method: body ? 'POST' : 'GET', credentials:'same-origin', cache:'no-store',
        headers: {'Accept':'application/json', ...(body ? {'Content-Type':'application/json'} : {})},
        ...(body ? {body:JSON.stringify(body)} : {}),
        signal:controller.signal
      });
      const data = await response.json();
      if (!response.ok) throw Error(data.description || data.message || 'Your cart could not be updated.');
      return data;
    } finally {clearTimeout(timer);}
  }
  function setBusy(value) {
    busy = value;
    document.documentElement.dataset.cbrsCartBusy = String(value);
    document.querySelectorAll('.atc-with-add-products, [name="checkout"]').forEach(el => {
      if (value) {el.dataset.cbrsWasDisabled = String(el.disabled); el.disabled = true;}
      else if (el.dataset.cbrsWasDisabled !== undefined) {
        el.disabled = el.dataset.cbrsWasDisabled === 'true';
        delete el.dataset.cbrsWasDisabled;
      }
    });
  }
  async function add(form) {
    if (busy) return;
    const root = form.closest('[data-cbrs-product]');
    if (!root) return;
    if (sessionStorage.getItem('cbrs-review-cart')) {
      message(root, 'Please review your cart before trying another add.', true); return;
    }
    const data = new FormData(form);
    const quantity = Number(data.get('quantity') || 1);
    const id = data.get('id');
    if (!id || !integer(quantity) || quantity < 1) {
      message(root,'Please select a variant and a valid quantity.'); return;
    }
    const radioProperties = {};
    for (const [name, value] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(name);
      if (match && typeof value === 'string') radioProperties[match[1]] = value;
      else if (value instanceof File && value.size) {
        message(root,'This product has a file option that needs the standard product form. Please contact us for help.'); return;
      }
    }
    const parent = {id:String(id),quantity,properties:radioProperties};
    if (data.get('selling_plan')) parent.selling_plan = data.get('selling_plan');
    const items = [parent], seen = new Set();
    const purchaseGroup = crypto.randomUUID();
    const isRadio = root.dataset.cbrsRadio === 'true';
    for (const box of root.querySelectorAll('.add_prod_checkbox')) {
      if (!box.checked || !isVisible(box)) continue;
      const variant = box.dataset.value;
      if (!variant || seen.has(variant)) continue;
      seen.add(variant);
      const input = box.closest('.shopify-product-form-additional')?.querySelector('.quantity-selector__input');
      const count = Number(input?.value || 1);
      if (!integer(count) || count < 1) {
        message(root,'Please enter a valid quantity for each selected option.'); return;
      }
      const item = {id:variant,quantity:count};
      // Exact existing service mapping; unrelated add-ons remain independent.
      if (isRadio && variant === '8135080411238') {
        if (count > quantity) {
          message(root,'Please select no more than one Pro Tune service for each radio.'); return;
        }
        const common = {_cbrs_version:'1',_cbrs_group:purchaseGroup,_cbrs_parent_variant:String(id)};
        Object.assign(parent.properties, common, {_cbrs_role:'radio'});
        item.properties = {...common,_cbrs_role:'tune',_cbrs_radio_title:root.dataset.cbrsTitle};
      }
      items.push(item);
    }
    setBusy(true);
    try {
      await request('cart/add.js', {items});
      const cart = await request('cart.js');
      // Validate grouped lines without assuming a bundle's component count is radio quantity.
      if (parent.properties._cbrs_group) {
        const found = cart.items.filter(x => group(x) === purchaseGroup);
        const p = found.find(x => role(x) === 'radio' && String(x.variant_id) === String(id));
        const tune = found.find(x => role(x) === 'tune');
        if (!p || !tune || p.quantity !== quantity || tune.quantity > p.quantity) {
          throw Error('Please review the package and its tuning service in your cart before continuing.');
        }
      }
      location.assign(base + 'cart');
    } catch (error) {
      // Never retry a mutation automatically. Even a failed request can have changed stock/cart.
      sessionStorage.setItem('cbrs-review-cart','1');
      try {await request('cart.js');} catch (_) { /* Leave review flag set. */ }
      message(root, error.name === 'AbortError'
        ? "We couldn't confirm the cart update. Please review your cart before trying again."
        : error.message, true);
      setBusy(false);
    }
  }
  async function change(row, quantity) {
    if (busy) return;
    const host = row.closest('form') || document.querySelector('main') || document.body;
    setBusy(true);
    try {
      const cart = await request('cart.js');
      const updates = planChange(cart, row.dataset.cartitemKey, quantity);
      const current = cart.items.find(x => x.key === row.dataset.cartitemKey);
      if (Number(quantity) > current.quantity) {
        // update.js does not validate inventory for existing variants.
        // Increases never alter another line, so use the item-level endpoint.
        await request('cart/change.js', {id:current.key,quantity:Number(quantity)});
      } else {
        // Bulk decreases keep a radio and its attached services together.
        await request('cart/update.js', {updates});
      }
      const after = await request('cart.js');
      for (const [key, expected] of Object.entries(updates)) {
        if ((after.items.find(x => x.key === key)?.quantity || 0) !== expected) {
          throw Error('Please review your cart: the requested quantities could not be confirmed.');
        }
      }
      location.reload();
    } catch (error) {
      try {
        const actual = await request('cart.js');
        for (const node of document.querySelectorAll('[data-cartitem-key]')) {
          const current = actual.items.find(x => x.key === node.dataset.cartitemKey);
          const input = node.querySelector('[data-quantity-input]');
          if (current && input) input.value = String(current.quantity);
        }
      } catch (_) { /* The error stays visible. */ }
      message(host,error.message,true);
      setBusy(false);
    }
  }
  window.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    const button = target.closest('.atc-with-add-products');
    if (button?.closest('[data-cbrs-product]')) {
      stop(event);
      const form = button.closest('form[data-product-form]');
      if (form) void add(form);
      return;
    }
    if (busy && target.closest('[name="checkout"],.additional-checkout-buttons')) {stop(event); return;}
    const row = target.closest('[data-cartitem-key]');
    if (!row) return;
    const remove = target.closest('[data-cartitem-remove]');
    const minus = target.closest('[data-quantity-minus]');
    const plus = target.closest('[data-quantity-plus]');
    if (remove || minus || plus) {
      stop(event);
      const input = row.querySelector('[data-quantity-input]');
      const quantity = remove ? 0 : Math.max(1,Number(input.value) + (plus ? 1 : -1));
      void change(row,quantity);
    }
  },true);
  window.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches('form[data-product-form]') && form.querySelector('.atc-with-add-products')) {
      stop(event); void add(form);
    } else if (busy) stop(event);
  },true);
  window.addEventListener('change', event => {
    const input = event.target;
    const row = input.closest?.('[data-cartitem-key]');
    if (row && input.matches('[data-quantity-input]')) {
      stop(event); void change(row,Number(input.value));
    }
  },true);
  // Visiting the actual cart is the explicit review step after an uncertain add.
  if (location.pathname.replace(/\/$/,'').endsWith('/cart') && sessionStorage.getItem('cbrs-review-cart')) {
    request('cart.js').then(() => {
      const host = document.querySelector('form[action*="/cart"]') || document.querySelector('main') || document.body;
      message(host,'Please check the items and quantities below. No failed add has been retried automatically.');
      sessionStorage.removeItem('cbrs-review-cart');
    }).catch(() => {});
  }
})();
